import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Security,
  ArrowBack,
  Visibility,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import DataTable from '@/components/DataTable/DataTable';
import DefaultRoleBadge from '@/components/DefaultRoleBadge/DefaultRoleBadge';
import RoleCustomPaymentTypeAccessSummary from '@/components/RoleCustomPaymentTypeAccessSummary/RoleCustomPaymentTypeAccessSummary';

import RoleFormDialog from './components/RoleFormDialog';
import RolePreviewDialog from './components/RolePreviewDialog';

import { useCreateRole } from '@/api/hooks/roles/useCreateRole';
import { useUpdateRole } from '@/api/hooks/roles/useUpdateRole';
import { useDeleteRole } from '@/api/hooks/roles/useDeleteRole';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import CpdActionButton from '@/components/ui/CpdActionButton';

const RoleManagement = () => {
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [selectedRole, setSelectedRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { createRole, loading: createLoading } = useCreateRole();
  const { updateRole, loading: updateLoading } = useUpdateRole();
  const { deleteRole, loading: deleteLoading } = useDeleteRole();

  const actionLoading = createLoading || updateLoading || deleteLoading;
  const externalLoading = actionLoading;

  const handleCreateRole = () => {
    setSelectedRole(null);
    setDialogOpen(true);
  };

  const handleEditRole = (role) => {
    navigate(`/admin/roles/${role.id}`);
  };

  const handleDeleteRole = (role) => {
    const hasActiveUsers = role.assigned_users_count > 0;

    let confirmMessage;
    if (hasActiveUsers) {
      confirmMessage = `Cannot delete role "${role.name}" because it is currently assigned to ${role.assigned_users_count} user(s). Please reassign users before deleting.`;
    } else {
      confirmMessage = `Are you sure you want to delete the role "${role.name}"? This action cannot be undone. The role and all its permissions will be permanently deleted.`;
    }

    if (hasActiveUsers) {
      setErrorMessage(confirmMessage);
      setShowErrorAlert(true);
      return;
    }

    requestConfirmation(confirmMessage, () => confirmDeleteRole(role));
  };

  const handleCloneRole = async (role) => {
    try {
      const clonedRoleData = {
        name: `${role.name} (Copy)`,
        description: role.description,
        organization_id: role.organization_id,
        facility_permissions: role.facility_permissions || {},
        organization_permissions: role.organization_permissions || {},
        clone_source_role_id: role.id,
      };

      const newRole = await createRole(clonedRoleData);

      dataTableRef.current?.refresh();

      setSuccessMessage(
        `Role "${newRole.name}" has been successfully cloned from "${role.name}".`,
      );
      setShowSuccessAlert(true);

      setTimeout(() => {
        const successAlert = document.getElementById('success-alert');
        if (successAlert) {
          successAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (err) {
      console.error('Error cloning role:', err);
      const nextErrorMessage =
        err.response?.data?.message || err.message || 'Failed to clone role';
      setErrorMessage(nextErrorMessage);
      setShowErrorAlert(true);
    }
  };

  const handlePreviewRole = (role) => {
    setSelectedRole(role);
    setPreviewDialogOpen(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  };

  const handleErrorClose = () => {
    setShowErrorAlert(false);
    setErrorMessage('');
  };

  const confirmDeleteRole = async (role) => {
    try {
      await deleteRole(role.id);

      dataTableRef.current?.refresh();

      setSuccessMessage(`Role "${role.name}" has been successfully deleted.`);
      setShowSuccessAlert(true);

      setTimeout(() => {
        const successAlert = document.getElementById('success-alert');
        if (successAlert) {
          successAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (err) {
      console.error('Error deleting role:', err);
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to delete role';
      setErrorMessage(errorMessage);
      setShowErrorAlert(true);

      setTimeout(() => {
        const errorAlert = document.getElementById('error-alert');
        if (errorAlert) {
          errorAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleSaveRole = async (roleData) => {
    try {
      if (selectedRole) {
        await updateRole(selectedRole.id, roleData);
        dataTableRef.current?.refresh();
      } else {
        const newRole = await createRole(roleData);
        setDialogOpen(false);
        navigate(`/admin/roles/${newRole.id}`);
        return;
      }
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: 'Search',
        filterType: 'search',
        placeholder: 'Search roles by name or description...',
        debounce: true,
        debounceDelay: 300,
      },
      {
        key: 'organization_id',
        label: 'Organization',
        filterType: 'select',
        loadOptions: () => OrganizationsApi.getOrganizations(),
        optionsTransform: (data) => [
          { value: '', label: 'All Organizations' },
          ...(data?.organizations || []).map((org) => ({
            value: org.id,
            label: org.name,
          })),
        ],
      },
    ],
    [],
  );

  const roleColumns = [
    {
      key: 'name',
      label: 'Role Name',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color='primary' fontSize='small' />
          <Typography variant='body2' fontWeight='medium'>
            {value}
          </Typography>
          <DefaultRoleBadge role={row} />
        </Box>
      ),
    },
    {
      key: 'organization_name',
      label: 'Organization',
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <Typography variant='body2' color='text.secondary'>
          {value}
        </Typography>
      ),
    },
    {
      key: 'assigned_users_count',
      label: 'Users',
      sortable: true,
      align: 'center',
      render: (value) => <Chip size='small' label={value} />,
    },
    {
      key: 'custom_payment_type_access_enabled',
      label: 'Custom Payment Types',
      sortable: false,
      render: (_value, row) => (
        <RoleCustomPaymentTypeAccessSummary role={row} />
      ),
    },
    {
      key: 'updated_at',
      label: 'Last Updated',
      sortable: true,
      format: 'date',
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title='Preview Permissions'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewRole(row);
              }}>
              <Visibility fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Edit Role'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleEditRole(row);
              }}>
              <Edit fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Clone Role'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleCloneRole(row);
              }}
              disabled={actionLoading}>
              {actionLoading ? (
                <CircularProgress size={16} />
              ) : (
                <ContentCopy fontSize='small' />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete Role'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRole(row);
              }}
              disabled={row.assigned_users_count > 0 || actionLoading}>
              <Delete
                fontSize='small'
                color={row.assigned_users_count > 0 ? 'disabled' : 'error'}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate('/admin/usermanagement')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBack sx={{ color: 'white' }} />
              </IconButton>
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500, flex: 1 }}
                component='h4'>
                Role Management
              </Typography>
              <CpdActionButton
                startIcon={<Add sx={{ color: 'white' }} />}
                onClick={handleCreateRole}
                variant='outlined'>
                Create Role
              </CpdActionButton>
            </Box>
          </CardHeader>
        }>
        {showSuccessAlert && successMessage && (
          <Alert
            id='success-alert'
            severity='success'
            sx={{ mb: 3 }}
            onClose={handleSuccessClose}>
            {successMessage}
          </Alert>
        )}

        {showErrorAlert && errorMessage && (
          <Alert
            id='error-alert'
            severity='error'
            sx={{ mb: 3 }}
            onClose={handleErrorClose}>
            {errorMessage}
          </Alert>
        )}

        <DataTable
          ref={dataTableRef}
          columns={roleColumns}
          endpoint='/roles'
          initialSort={{ key: 'updated_at', order: 'desc' }}
          pageSize={50}
          autoFetch={true}
          enableColumnPicker={true}
          enableSorting={true}
          enablePagination={false}
          enableFilters={true}
          enableUrlPersistence={true}
          persistenceKey='roles'
          columnFilters={columnFilters}
          externalLoading={externalLoading}
          renderEmptyState={() => (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '3rem' }}>
                <Typography variant='h6' color='text.secondary'>
                  No roles found
                </Typography>
              </td>
            </tr>
          )}
        />
      </PageLayout>

      <RoleFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        role={selectedRole}
        onSave={handleSaveRole}
        loading={actionLoading}
      />

      <RolePreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        role={selectedRole}
      />
    </>
  );
};

export default RoleManagement;
